import { memo, useCallback, useContext } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import type { BarChart } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { trunc } from '@trinity/chat-components/utils';
import { ChartHoverText } from './Hover';
import { theme } from './theme';
import { formatValueWithUnit } from '@trinity/chat-components/utils/chart';
import { formatAxisLabel } from './utils';
import { TemplateTooltip } from './TemplateTooltip';
import { replaceTemplateValues } from './template-utils';

type Props = {
  chart: BarChart;
  colors: string[];
  animate?: boolean;
  columnN?: number;
  unit?: string;
  isPrefix?: boolean;
  isInteractive?: boolean;
};

export const NivoBarChart = memo(({ chart, colors, animate = false, columnN, unit, isPrefix, isInteractive = true }: Props) => {
  const returnedData = chart.data;
  const data = [];
  const keys = new Set<string>();

  for (let i = 0; i < returnedData.length; i++) {
    const item = {};
    item[chart.indexBy] = returnedData[i].x;
    keys.add(returnedData[i].x);
    item[returnedData[i].x] = returnedData[i].y;
    item[`${returnedData[i].x}Color`] = colors[i];
    data.push(item);
  }

  data.reverse();

  const [open, setOpen] = useContext(LegendCollapseToggleContext);

  const formatLeftAxisLabel = useCallback((value: string) => {
    return trunc(value, 10);
  }, []);

  const formatBottomAxisLabel = useCallback((value: string) => {
    return formatAxisLabel({ value, unit, isPrefix });
  }, [unit, isPrefix]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <ResponsiveBar
        data={data}
        keys={[...keys]}
        indexBy={chart.indexBy}
        margin={{ top: 10, right: 20, bottom: 50, left: 80 }}
        padding={0.3}
        valueScale={{ type: 'linear', min: 'auto' }}
        indexScale={{ type: 'band', round: true }}
        colors={colors}
        enableGridY={false}
        isInteractive={isInteractive}
        borderColor={{
          from: 'color',
          modifiers: [
            [
              'darker',
              1.6,
            ],
          ],
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: chart.yAxisTitle,
          legendPosition: 'middle',
          legendOffset: 32,
          truncateTickAt: 20,
          format: formatBottomAxisLabel,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          // legend: props.chart.yAxisTitle,
          legendPosition: 'middle',
          format: formatLeftAxisLabel,
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor="#ffffff"
        label={d => formatValueWithUnit({ value: d.value, unit, isPrefix })}
        layout="horizontal"
        tooltip={isInteractive ? (e => {
          // If a tooltip template is provided, use it
          if (chart.tooltipTemplate) {
            const templateValues = {
              xAxisLabel: e.indexValue,
              xValue: e.indexValue,
              yValue: e.value,
              yAxisLabel: chart.yAxisTitle,
              yAxisTitle: chart.yAxisTitle,
              xAxisTitle: chart.xAxisTitle,
              value: e.value,
              label: e.indexValue,
              unit: unit || '',
            };
            const content = replaceTemplateValues(chart.tooltipTemplate, templateValues);
            return <TemplateTooltip content={content} />;
          }
          // Otherwise use the default tooltip
          return <ChartHoverText
            name={e.indexValue}
            count={e.value}
            columnN={columnN}
            unit={unit}
            isPrefix={isPrefix} />;
        }) : undefined}
        onClick={isInteractive ? (data => {
          setOpen(true);
        }) : undefined}
        animate={animate}
        theme={theme}
        role="application" />
    </div>
  );
});